import {
  useEditDesignSubmittalMutation,
  useGetDesignSubmittalsMetadataQuery,
  useGetDesignSubmittalsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { useCheckPermission } from 'hooks/useCheckPermission'
import { Permission } from '@cims-online/shared'
import { useCIMSModalValues } from 'components/CIMSModal/hooks/useCIMSModal'
import { CreateDesignSubmittalModalData } from '../components/DesignSubmittalLogTable/hooks/useCreateOptions/components/CreateDesignSubmittalModal'
import { WithPayItemGlobalId } from 'components/CIMSTable/components/HeaderActionButtons/types'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { DesignSubmittal, DesignSubmittalLogContext } from './types'
import { useProjectContext } from 'context/ProjectContext'

const { Provider, useContextFn } =
  createStrictContext<DesignSubmittalLogContext>('DesignSubmittalLogContext')

export const useDesignSubmittalLogContext = useContextFn

export const DesignSubmittalLogProvider: React.FC<WithPayItemGlobalId> = ({
  payItemGlobalId,
  children,
}) => {
  const { projectId } = useProjectContext()
  const {
    data,
    refetch,
    loading: fetching,
  } = useGetDesignSubmittalsQuery({
    ...DefaultQueryHookOptions,
    variables: {
      projectId,
      input: { projectId: { equals: projectId } },
    },
  })
  const { data: metadata, loading: metaFetching } =
    useGetDesignSubmittalsMetadataQuery({
      ...DefaultQueryHookOptions,
      variables: { globalId: projectId },
    })
  const [edit] = useEditDesignSubmittalMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editDesignSubmittal: {
        designSubmittal: { sharePointFolderName },
      },
    }) => simpleEditNotification(sharePointFolderName),
  })

  const advancementModal = useCIMSModalValues<DesignSubmittal>()
  const revertModal = useCIMSModalValues<DesignSubmittal>()
  const createModal = useCIMSModalValues<CreateDesignSubmittalModalData>()

  const actionsOptions = useOptions({
    items: metadata?.project?.dsActions,
    value: 'id',
    label: 'name',
    sort: 'name',
    tooltip: 'description',
  })
  const disciplinesOptions = useOptions({
    items: metadata?.project?.dsDisciplines,
    value: 'id',
    label: 'name',
    sort: 'name',
    tooltip: 'description',
  })
  const fdcOptions = useOptions({
    items: metadata?.project?.fieldDesignChanges,
    value: 'id',
    label: 'formattedNumber',
    sort: 'formattedNumber',
  })
  const orgsOptions = useOptions({
    items: metadata?.project?.dsOrganizations,
    value: 'id',
    label: 'name',
    tooltip: 'description',
    sort: 'name',
  })
  const typesOptions = useOptions({
    items: metadata?.project?.dsTypes,
    value: 'id',
    label: 'name',
    sort: 'name',
    tooltip: 'description',
  })

  const canDisposition = useCheckPermission(
    Permission.GiveDispositionDesignSubmittal
  )
  const canNumberName = useCheckPermission(
    Permission.EditNumberNameDesignSubmittal
  )
  const canReviewAll = useCheckPermission(Permission.ReviewAllDesignSubmittal)
  const canRoute = useCheckPermission(Permission.RouteDesignSubmittal)

  return (
    <Provider
      value={{
        payItemGlobalId,
        records: data?.designSubmittals?.records ?? [],
        refetch,
        fetching,
        edit: async (input) => {
          await edit({ variables: { input } })
        },
        allPackageNumbers:
          data?.designSubmittals?.records?.map(({ packageNumber }) =>
            packageNumber.trim()
          ) ?? [],
        metaFetching,
        advancementModal,
        revertModal,
        createModal,
        selectOptions: {
          actionsOptions,
          disciplinesOptions,
          fdcOptions,
          orgsOptions,
          typesOptions,
        },
        permissions: {
          canDisposition,
          canNumberName,
          canReviewAll,
          canRoute,
        },
        quickLinks: metadata?.project?.dsQuickLinks,
      }}
    >
      {children}
    </Provider>
  )
}
